import styles from './Modal.module.css';

import type { FC, ReactNode } from 'react';
import type { ColorVariant } from 'src/theme';

import React, { useId, useRef, useEffect } from 'react';

import { isDismissKey, focusAfterTab } from 'src/utils/dialogKeys';

import { Image } from 'src/components/themed/Image';
import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { Typography } from 'src/components/themed/Typography';

type ModalProps = {
  visible: boolean;
  onClose: () => void;
  icon?: ReactNode;
  image?: string;
  header?: string;
  description?: string;
  /**
   * What a screen reader announces when the dialog opens. Used only when there is no visible
   * `description`/`header` to point at — a modal with neither (the filter panel) must say what it
   * is here.
   */
  label?: string;
  buttonText?: string;
  buttonVariant?: ColorVariant;
  onSubmit?: () => void;
  disabled?: boolean;
  buttonIcon?: ReactNode;
  children?: ReactNode;
};

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The one dialog component behind every modal on the storefront — the filter panel, the payment
 * and confirmation modals of both verticals. It behaves like a dialog (BUG-14, BUG-18):
 *
 *  - announced as `role="dialog"` with `aria-modal` and a name;
 *  - Escape closes it, exactly as the backdrop tap does — `onClose` is the caller's own handler, so a
 *    payment modal that guards its close during a transaction keeps that guard for Escape too;
 *  - the focus moves into the dialog when it opens, stays inside it while Tab cycles, and returns to
 *    the element that opened it when it closes.
 */
export const Modal: FC<ModalProps> = (props) => {
  const {
    visible,
    onClose,
    header,
    description,
    label,
    icon,
    image,
    buttonText,
    onSubmit,
    disabled,
    buttonIcon,
    children,
    buttonVariant,
  } = props;

  const dialogRef = useRef<HTMLDivElement>(null);
  // The latest close handler, read at key time. Callers pass a fresh closure on every render
  // (`onClose={() => setVisible(false)}`, a modal's own `handleClose`), and keying the effect on it
  // would re-run the open sequence on every keystroke into the dialog's fields — pulling the focus
  // (and the phone keyboard) off the field the buyer is typing in.
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  });
  const headerId = useId();
  const descriptionId = useId();

  // The dialog is announced by its visible title when it has one; the plain `label` is the fallback.
  const labelledBy = description ? descriptionId : header ? headerId : undefined;
  const ariaLabel = labelledBy ? undefined : label;

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    const dialog = dialogRef.current;
    // Captured once per open: this is where the focus goes back when the dialog closes.
    const opener = document.activeElement as HTMLElement | null;

    // Focus the container rather than the first field: on a phone a focused input pops the keyboard
    // over a dialog the buyer has not read yet. Tab from here goes to the first control.
    dialog?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (isDismissKey(event)) {
        const target = event.target as HTMLElement | null;
        // Escape belongs to whatever is in front: an open native <select> list (the browser
        // dismisses it and still dispatches the key here), or a sheet opened over this one — the
        // wallet window or the account panel, rendered inside this dialog's own markup (app#390).
        // Only a key aimed at this dialog itself — the innermost dialog under the key is this one
        // — or at nothing in particular closes it.
        const innermost = target?.closest('[role="dialog"]') ?? null;
        const aimedAtDialog = !target || target === document.body || innermost === dialog;

        if (!aimedAtDialog || target?.tagName === 'SELECT') {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab' || !dialog) {
        return;
      }

      const focusables = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      const active = document.activeElement as HTMLElement | null;
      const inside = active && dialog.contains(active) && active !== dialog ? active : null;
      const next = focusAfterTab(event, focusables, inside);

      if (next) {
        event.preventDefault();
        next.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Hand the focus back to whatever opened the dialog, if it is still on the page.
      if (opener && opener.isConnected && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <>
      <div className={styles.darkBG} onClick={onClose} aria-hidden="true" />
      <div className={styles.centered}>
        <div
          ref={dialogRef}
          className={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          aria-label={ariaLabel}
          tabIndex={-1}
        >
          <Stack gap={5}>
            <Image size="medium" icon={icon} src={image} alt="" className={styles.headerImage} />
            <Stack gap={1} alignItems="center">
              {header ? (
                <Typography color="lightWhite" id={headerId}>
                  {header}
                </Typography>
              ) : null}
              {description ? (
                <Typography color="white" fontWeight="bold" variant="largeTitle" id={descriptionId}>
                  {description}
                </Typography>
              ) : null}
            </Stack>
            {children}
            {buttonText && onSubmit ? (
              <Button
                title={buttonText}
                onClick={onSubmit}
                disabled={disabled}
                icon={buttonIcon}
                variant={buttonVariant || 'accent'}
              />
            ) : null}
          </Stack>
        </div>
      </div>
    </>
  );
};
